import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Modal, Field } from "./Rooms";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/format";
import {
    Receipt, CreditCard, Banknote, Link as LinkIcon, AlertTriangle,
    Download, FileText, RefreshCw, Settings, ArrowUpDown, Copy,
    Wallet, CheckCircle2, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const TABS = [
    { key: "folios",     label: "Guest folios" },
    { key: "overdue",    label: "Overdue" },
    { key: "saas",       label: "SaaS invoices" },
    { key: "revenue",    label: "Revenue" },
    { key: "settings",   label: "Tax / VAT" },
];

const METHOD_ICONS = {
    cash: Banknote,
    card: CreditCard,
    bank_transfer: ArrowUpDown,
    online: LinkIcon,
    room_charge: Wallet,
};

export default function Billing() {
    const { user } = useAuth();
    const [tab, setTab] = useState("folios");
    return (
        <>
            <Topbar title="Payments & Billing" subtitle="Guest folios · SaaS invoices · Revenue · Tax & VAT" />
            <div className="flex-1 overflow-auto p-4 md:p-6 space-y-4">
                <div className="flex items-center gap-1 border-b border-border" data-testid="billing-tabs">
                    {TABS.map((t) => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                                data-testid={`billing-tab-${t.key}`}
                                className={`-mb-px border-b-2 px-3 py-2.5 text-[12px] font-medium transition ${
                                    tab === t.key ? "border-[#ff7b02] text-[#ff7b02]" : "border-transparent text-muted-foreground hover:text-foreground"
                                }`}>
                            {t.label}
                        </button>
                    ))}
                </div>
                {tab === "folios"   && <FoliosTab />}
                {tab === "overdue"  && <OverdueTab />}
                {tab === "saas"     && <SaasTab user={user} />}
                {tab === "revenue"  && <RevenueTab />}
                {tab === "settings" && <SettingsTab />}
            </div>
        </>
    );
}

// ------------------------------------------------------------
// Folios — list reservations and open folio drawer
// ------------------------------------------------------------
function FoliosTab() {
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("all");
    const [drawer, setDrawer] = useState(null);
    const load = async () => {
        const { data } = await api.get("/reservations");
        setRows(data);
    };
    useEffect(() => { load(); }, []);
    const visible = useMemo(() => {
        if (filter === "all") return rows;
        return rows.filter((r) => r.payment_status === filter);
    }, [rows, filter]);
    return (
        <div className="space-y-3" data-testid="folios-tab">
            <div className="flex flex-wrap items-center gap-1">
                {["all", "pending", "partial", "paid"].map((f) => (
                    <button key={f} onClick={() => setFilter(f)}
                            data-testid={`folio-filter-${f}`}
                            className={`rounded-md border px-3 py-1.5 text-[12px] font-medium capitalize ${
                                filter === f ? "border-[#ff7b02] bg-[#ff7b02]/10 text-[#ff7b02]" : "border-border text-muted-foreground hover:bg-muted/40"
                            }`}>
                        {f} ({f === "all" ? rows.length : rows.filter((r) => r.payment_status === f).length})
                    </button>
                ))}
                <div className="ml-auto text-[11px] text-muted-foreground">{visible.length} reservations</div>
            </div>
            <div className="akm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Guest</th>
                            <th className="px-3 py-2 text-left">Stay</th>
                            <th className="px-3 py-2 text-left">Source</th>
                            <th className="px-3 py-2 text-right">Total</th>
                            <th className="px-3 py-2 text-right">Paid</th>
                            <th className="px-3 py-2 text-right">Balance</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2" />
                        </tr>
                    </thead>
                    <tbody data-testid="folios-table">
                        {visible.map((r) => {
                            const paid = r.paid_amount || 0;
                            const total = r.total_amount || 0;
                            const balance = Math.max(0, +(total - paid).toFixed(2));
                            return (
                                <tr key={r.id} onClick={() => setDrawer(r.id)}
                                    className="cursor-pointer border-t border-border hover:bg-muted/40">
                                    <td className="px-3 py-2 font-medium">{r.guest_first_name} {r.guest_last_name}</td>
                                    <td className="px-3 py-2 text-[12px] text-muted-foreground">{r.check_in} → {r.check_out}</td>
                                    <td className="px-3 py-2 text-[12px] capitalize text-muted-foreground">{(r.source || "direct").replace("_", " ")}</td>
                                    <td className="px-3 py-2 text-right">{formatCurrency(total)}</td>
                                    <td className="px-3 py-2 text-right text-muted-foreground">{formatCurrency(paid)}</td>
                                    <td className={`px-3 py-2 text-right font-semibold ${balance > 0 ? "text-red-700" : "text-emerald-700"}`}>{formatCurrency(balance)}</td>
                                    <td className="px-3 py-2">
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${
                                            r.payment_status === "paid" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" :
                                            r.payment_status === "partial" ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" :
                                            "bg-muted text-muted-foreground"
                                        }`}>{r.payment_status || "pending"}</span>
                                    </td>
                                    <td className="px-3 py-2 text-right"><ChevronRight size={14} className="text-muted-foreground" /></td>
                                </tr>
                            );
                        })}
                        {visible.length === 0 && <tr><td colSpan="8" className="py-10 text-center text-muted-foreground">No folios</td></tr>}
                    </tbody>
                </table>
            </div>
            {drawer && <FolioDrawer resId={drawer} onClose={() => setDrawer(null)} onReload={load} />}
        </div>
    );
}

// ------------------------------------------------------------
// Folio drawer — full payments UI
// ------------------------------------------------------------
function FolioDrawer({ resId, onClose, onReload }) {
    const [folio, setFolio] = useState(null);
    const [payOpen, setPayOpen] = useState(false);
    const [refundOpen, setRefundOpen] = useState(false);
    const [linkOpen, setLinkOpen] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/payments/reservations/${resId}/folio`);
            setFolio(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [resId]);
    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const downloadPdf = async () => {
        try {
            const base = process.env.REACT_APP_BACKEND_URL;
            const token = localStorage.getItem("akm_token");
            const r = await fetch(`${base}/api/payments/reservations/${resId}/invoice.pdf`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) throw new Error("PDF generation failed");
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `invoice-${resId.slice(0, 8)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(e.message); }
    };

    if (!folio) return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-foreground/30" onClick={onClose} />
            <div className="relative h-full w-full max-w-[720px] bg-background p-8 text-sm text-muted-foreground">Loading…</div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="folio-drawer">
            <div className="absolute inset-0 bg-foreground/30" onClick={onClose} />
            <div className="relative h-full w-full max-w-[760px] overflow-y-auto bg-background shadow-xl">
                <div className="sticky top-0 z-10 border-b border-border bg-background/95 px-5 py-3.5 backdrop-blur">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-sm font-semibold tracking-tight">
                                Folio · {folio.guest.first_name} {folio.guest.last_name}
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                                {folio.stay.check_in} → {folio.stay.check_out} · Room {folio.stay.room_number} · {folio.stay.room_type}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-[11px] text-muted-foreground">Balance</div>
                            <div className={`text-2xl font-semibold ${folio.balance > 0 ? "text-red-700" : "text-emerald-700"}`}>
                                {formatCurrency(folio.balance)}
                            </div>
                            <button onClick={onClose} className="akm-btn-ghost mt-1 text-[11px]">Close</button>
                        </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <button onClick={() => setPayOpen(true)} className="akm-btn-primary text-[11px]" data-testid="folio-pay-btn">
                            <Receipt size={12} /> Record payment
                        </button>
                        <button onClick={() => setRefundOpen(true)} disabled={folio.net_paid <= 0}
                                className="akm-btn-ghost text-[11px]" data-testid="folio-refund-btn">
                            <RefreshCw size={12} /> Refund
                        </button>
                        <button onClick={() => setLinkOpen(true)} className="akm-btn-ghost text-[11px]" data-testid="folio-link-btn">
                            <LinkIcon size={12} /> Payment link
                        </button>
                        <button onClick={downloadPdf} className="akm-btn-ghost text-[11px]" data-testid="folio-pdf-btn">
                            <Download size={12} /> Invoice PDF
                        </button>
                    </div>
                </div>

                <div className="space-y-4 px-5 py-4">
                    <div className="akm-card p-3">
                        <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Line items</div>
                        <table className="w-full text-[13px]">
                            <tbody>
                                {folio.line_items.map((li, i) => (
                                    <tr key={i} className="border-t border-border first:border-t-0">
                                        <td className="px-2 py-1.5">{li.description}</td>
                                        <td className="px-2 py-1.5 text-[11px] uppercase text-muted-foreground">{li.kind}</td>
                                        <td className="px-2 py-1.5 text-right font-medium">{formatCurrency(li.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t border-border">
                                    <td className="px-2 py-1.5 text-muted-foreground" colSpan="2">Subtotal</td>
                                    <td className="px-2 py-1.5 text-right">{formatCurrency(folio.subtotal)}</td>
                                </tr>
                                <tr>
                                    <td className="px-2 py-1.5 text-muted-foreground" colSpan="2">Tax ({(folio.tax_rate * 100).toFixed(1)}%)</td>
                                    <td className="px-2 py-1.5 text-right">{formatCurrency(folio.tax_amount)}</td>
                                </tr>
                                <tr className="font-semibold">
                                    <td className="px-2 py-1.5" colSpan="2">Grand total</td>
                                    <td className="px-2 py-1.5 text-right">{formatCurrency(folio.grand_total)}</td>
                                </tr>
                                <tr>
                                    <td className="px-2 py-1.5 text-emerald-700" colSpan="2">Paid</td>
                                    <td className="px-2 py-1.5 text-right text-emerald-700">{formatCurrency(folio.net_paid)}</td>
                                </tr>
                                <tr className="font-semibold">
                                    <td className={`px-2 py-1.5 ${folio.balance > 0 ? "text-red-700" : "text-emerald-700"}`} colSpan="2">Balance due</td>
                                    <td className={`px-2 py-1.5 text-right ${folio.balance > 0 ? "text-red-700" : "text-emerald-700"}`}>
                                        {formatCurrency(folio.balance)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <div>
                        <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                            Payments ({folio.payments.length}) · Refunds ({folio.refunds.length})
                        </div>
                        <div className="space-y-1">
                            {folio.payments
                                .slice()
                                .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
                                .map((p) => <PayRow key={p.id} p={p} />)}
                            {folio.refunds.map((p) => <PayRow key={p.id} p={p} refund />)}
                            {folio.payments.length + folio.refunds.length === 0 && (
                                <div className="rounded-md border border-dashed border-border bg-card/40 py-6 text-center text-[12px] text-muted-foreground">
                                    No payments recorded yet
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {payOpen && <PayForm resId={resId} balance={folio.balance} onClose={() => setPayOpen(false)}
                                  onSaved={async () => { setPayOpen(false); await load(); await onReload(); }} />}
            {refundOpen && <RefundForm resId={resId} maxRefund={folio.net_paid} onClose={() => setRefundOpen(false)}
                                        onSaved={async () => { setRefundOpen(false); await load(); await onReload(); }} />}
            {linkOpen && <PayLinkForm resId={resId} balance={folio.balance} onClose={() => setLinkOpen(false)}
                                       onSaved={() => setLinkOpen(false)} />}
        </div>
    );
}

function PayRow({ p, refund }) {
    const Icon = METHOD_ICONS[p.method] || Receipt;
    return (
        <div className="flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-[12px]" data-testid={`pay-row-${p.id}`}>
            <Icon size={12} className={refund ? "text-red-600" : "text-emerald-700"} />
            <div className="flex-1">
                <div className="font-medium">{refund ? "Refund · " : ""}{p.method.replace("_", " ")}</div>
                <div className="text-[10px] text-muted-foreground">
                    {formatDateTime(p.created_at)} · by {p.author_name || "—"}{p.reference ? ` · ${p.reference}` : ""}
                </div>
            </div>
            <div className={`font-semibold ${refund ? "text-red-700" : "text-emerald-700"}`}>
                {refund ? "−" : "+"}{formatCurrency(p.amount)}
            </div>
        </div>
    );
}

function PayForm({ resId, balance, onClose, onSaved }) {
    const [f, setF] = useState({ amount: balance, method: "cash", reference: "", note: "" });
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.post(`/payments/reservations/${resId}/payments`, f);
            toast.success(`Recorded ${formatCurrency(f.amount)} · balance ${formatCurrency(data.balance)}`);
            onSaved();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setSaving(false); }
    };
    return (
        <Modal title="Record payment" onClose={onClose}>
            <form onSubmit={submit} className="space-y-3" data-testid="pay-form">
                <Field label="Amount">
                    <input required type="number" step="0.01" min="0.01" className="akm-input"
                           value={f.amount} onChange={(e) => setF({ ...f, amount: parseFloat(e.target.value) || 0 })}
                           data-testid="pay-amount" />
                </Field>
                <Field label="Method">
                    <select className="akm-input" value={f.method}
                            onChange={(e) => setF({ ...f, method: e.target.value })}
                            data-testid="pay-method">
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="bank_transfer">Bank transfer</option>
                        <option value="online">Online</option>
                    </select>
                </Field>
                <Field label="Reference (optional)">
                    <input className="akm-input" value={f.reference}
                           onChange={(e) => setF({ ...f, reference: e.target.value })}
                           placeholder="Receipt #, terminal ID, etc." />
                </Field>
                <Field label="Note (optional)">
                    <input className="akm-input" value={f.note}
                           onChange={(e) => setF({ ...f, note: e.target.value })} />
                </Field>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">Cancel</button>
                    <button type="submit" disabled={saving} className="akm-btn-primary" data-testid="pay-submit">
                        {saving ? "Saving…" : "Record payment"}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

function RefundForm({ resId, maxRefund, onClose, onSaved }) {
    const [f, setF] = useState({ amount: maxRefund, method: "card", reference: "", note: "" });
    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/payments/reservations/${resId}/refund`, f);
            toast.success(`Refunded ${formatCurrency(f.amount)}`);
            onSaved();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    return (
        <Modal title="Issue refund" onClose={onClose}>
            <form onSubmit={submit} className="space-y-3" data-testid="refund-form">
                <div className="text-[12px] text-muted-foreground">Max refundable: {formatCurrency(maxRefund)}</div>
                <Field label="Amount">
                    <input required type="number" step="0.01" min="0.01" max={maxRefund} className="akm-input"
                           value={f.amount} onChange={(e) => setF({ ...f, amount: parseFloat(e.target.value) || 0 })}
                           data-testid="refund-amount" />
                </Field>
                <Field label="Method">
                    <select className="akm-input" value={f.method}
                            onChange={(e) => setF({ ...f, method: e.target.value })}>
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="bank_transfer">Bank transfer</option>
                    </select>
                </Field>
                <Field label="Note"><input className="akm-input" value={f.note}
                                            onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">Cancel</button>
                    <button type="submit" className="akm-btn-primary" data-testid="refund-submit">Issue refund</button>
                </div>
            </form>
        </Modal>
    );
}

function PayLinkForm({ resId, balance, onClose }) {
    const [f, setF] = useState({ amount: balance, description: "", expires_in_days: 7 });
    const [link, setLink] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post(`/payments/reservations/${resId}/payment-link`, f);
            const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
            setLink({ ...data, url: `${base.replace(/\/$/, "")}/pay/${data.token}` });
            toast.success("Payment link created");
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    return (
        <Modal title="Create payment link" onClose={onClose}>
            {!link ? (
                <form onSubmit={submit} className="space-y-3" data-testid="link-form">
                    <Field label="Amount">
                        <input required type="number" step="0.01" min="0.01" className="akm-input"
                               value={f.amount} onChange={(e) => setF({ ...f, amount: parseFloat(e.target.value) || 0 })} />
                    </Field>
                    <Field label="Description (optional)">
                        <input className="akm-input" value={f.description}
                               onChange={(e) => setF({ ...f, description: e.target.value })} />
                    </Field>
                    <Field label="Expires in (days)">
                        <input type="number" min="1" max="30" className="akm-input" value={f.expires_in_days}
                               onChange={(e) => setF({ ...f, expires_in_days: parseInt(e.target.value, 10) || 7 })} />
                    </Field>
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="akm-btn-ghost">Cancel</button>
                        <button type="submit" className="akm-btn-primary" data-testid="link-submit">Create link</button>
                    </div>
                </form>
            ) : (
                <div className="space-y-3">
                    <div className="text-[12px] text-muted-foreground">Share this link with the guest:</div>
                    <div className="flex items-center gap-2">
                        <input readOnly className="akm-input flex-1 font-mono text-[11px]" value={link.url} />
                        <button onClick={() => { navigator.clipboard.writeText(link.url); toast.success("Copied"); }}
                                className="akm-btn-ghost text-[11px]" data-testid="link-copy">
                            <Copy size={11} /> Copy
                        </button>
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                        Amount {formatCurrency(link.amount)} · expires {formatDate(link.expires_at)}
                    </div>
                    <div className="flex justify-end pt-2">
                        <button onClick={onClose} className="akm-btn-primary">Done</button>
                    </div>
                </div>
            )}
        </Modal>
    );
}

// ------------------------------------------------------------
// Overdue
// ------------------------------------------------------------
function OverdueTab() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/payments/overdue").then(({ data }) => setData(data)); }, []);
    if (!data) return <div>Loading…</div>;
    return (
        <div className="space-y-3" data-testid="overdue-tab">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                <div className="akm-card p-3">
                    <div className="text-[11px] uppercase text-muted-foreground">Outstanding</div>
                    <div className="text-2xl font-semibold text-red-700">{formatCurrency(data.total_outstanding)}</div>
                </div>
                <div className="akm-card p-3">
                    <div className="text-[11px] uppercase text-muted-foreground">Overdue reservations</div>
                    <div className="text-2xl font-semibold">{data.count}</div>
                </div>
            </div>
            <div className="akm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Guest</th>
                            <th className="px-3 py-2 text-left">Check-out</th>
                            <th className="px-3 py-2 text-right">Days overdue</th>
                            <th className="px-3 py-2 text-right">Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.items.map((r) => (
                            <tr key={r.reservation_id} className="border-t border-border" data-testid={`overdue-${r.reservation_id}`}>
                                <td className="px-3 py-2 font-medium">{r.guest_first_name} {r.guest_last_name}</td>
                                <td className="px-3 py-2 text-[12px] text-muted-foreground">{r.check_out}</td>
                                <td className="px-3 py-2 text-right">
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                        r.days_overdue > 30 ? "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300" :
                                        r.days_overdue > 7 ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" :
                                        "bg-muted text-muted-foreground"
                                    }`}>{r.days_overdue}d</span>
                                </td>
                                <td className="px-3 py-2 text-right font-semibold text-red-700">{formatCurrency(r.balance)}</td>
                            </tr>
                        ))}
                        {data.items.length === 0 && (
                            <tr><td colSpan="4" className="py-10 text-center text-emerald-700">
                                <CheckCircle2 className="inline mr-1" size={14} /> No overdue payments
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ------------------------------------------------------------
// SaaS invoices
// ------------------------------------------------------------
function SaasTab({ user }) {
    const [rows, setRows] = useState([]);
    useEffect(() => { api.get("/invoices").then(({ data }) => setRows(data)); }, []);
    const downloadPdf = async (id) => {
        try {
            const base = process.env.REACT_APP_BACKEND_URL;
            const token = localStorage.getItem("akm_token");
            const r = await fetch(`${base}/api/payments/saas-invoices/${id}/pdf`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) throw new Error("PDF failed");
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `saas-${id.slice(0, 8)}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(e.message); }
    };
    return (
        <div className="space-y-3" data-testid="saas-tab">
            <div className="text-[12px] text-muted-foreground">
                {user?.role === "super_admin" ? "All hotel subscription invoices" : "Your subscription invoices from Akomoda"}
            </div>
            <div className="akm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Invoice</th>
                            <th className="px-3 py-2 text-left">Period</th>
                            <th className="px-3 py-2 text-right">Amount</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-right" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((i) => (
                            <tr key={i.id} className="border-t border-border" data-testid={`saas-row-${i.id}`}>
                                <td className="px-3 py-2 font-mono text-[11px]">{i.invoice_number}</td>
                                <td className="px-3 py-2 text-[12px] text-muted-foreground">{i.period_start} → {i.period_end}</td>
                                <td className="px-3 py-2 text-right">{formatCurrency(i.amount)} {i.currency}</td>
                                <td className="px-3 py-2">
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${
                                        i.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                                    }`}>{i.status}</span>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => downloadPdf(i.id)} className="akm-btn-ghost text-[11px]">
                                        <Download size={11} /> PDF
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan="5" className="py-10 text-center text-muted-foreground">No SaaS invoices</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ------------------------------------------------------------
// Revenue
// ------------------------------------------------------------
function RevenueTab() {
    const today = new Date().toISOString().slice(0, 10);
    const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
    const [range, setRange] = useState({ from: monthAgo, to: today });
    const [data, setData] = useState(null);
    const load = async () => {
        const { data } = await api.get(`/payments/reports/revenue?date_from=${range.from}&date_to=${range.to}`);
        setData(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [range.from, range.to]);
    if (!data) return <div>Loading…</div>;
    const maxBar = Math.max(1, ...data.by_day.map((d) => d.amount));
    return (
        <div className="space-y-3" data-testid="revenue-tab">
            <div className="flex flex-wrap items-center gap-2">
                <input type="date" className="akm-input max-w-[160px]" value={range.from}
                       onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="rev-from" />
                <span className="text-muted-foreground">→</span>
                <input type="date" className="akm-input max-w-[160px]" value={range.to}
                       onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="rev-to" />
                <button onClick={load} className="akm-btn-ghost text-[11px]"><RefreshCw size={11} /> Reload</button>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <div className="akm-card p-3"><div className="text-[11px] uppercase text-muted-foreground">Net revenue</div><div className="text-xl font-semibold text-emerald-700">{formatCurrency(data.net_revenue)}</div></div>
                <div className="akm-card p-3"><div className="text-[11px] uppercase text-muted-foreground">Refunded</div><div className="text-xl font-semibold text-red-700">{formatCurrency(data.total_refunded)}</div></div>
                <div className="akm-card p-3"><div className="text-[11px] uppercase text-muted-foreground">POS revenue</div><div className="text-xl font-semibold">{formatCurrency(data.pos_revenue)}</div></div>
                <div className="akm-card p-3"><div className="text-[11px] uppercase text-muted-foreground">Payments</div><div className="text-xl font-semibold">{data.payments_count}</div></div>
            </div>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                <div className="akm-card p-3 lg:col-span-2">
                    <div className="mb-2 text-[11px] uppercase text-muted-foreground">Daily revenue</div>
                    <div className="flex h-32 items-end gap-1">
                        {data.by_day.length === 0 && <div className="text-[12px] text-muted-foreground">No data</div>}
                        {data.by_day.map((d) => (
                            <div key={d.date} className="group flex-1" title={`${d.date}: ${formatCurrency(d.amount)}`}>
                                <div className="w-full rounded-t bg-[#ff7b02] transition group-hover:bg-[#cc6202]"
                                     style={{ height: `${(d.amount / maxBar) * 100}%` }} />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="akm-card p-3">
                    <div className="mb-2 text-[11px] uppercase text-muted-foreground">By payment method</div>
                    {Object.entries(data.by_method).map(([k, v]) => (
                        <div key={k} className="flex justify-between border-t border-border py-1 text-[12px] first:border-t-0">
                            <span className="capitalize">{k.replace("_", " ")}</span>
                            <span className="font-medium">{formatCurrency(v)}</span>
                        </div>
                    ))}
                    {Object.keys(data.by_method).length === 0 && <div className="text-[12px] text-muted-foreground">No payments</div>}
                </div>
            </div>
        </div>
    );
}

// ------------------------------------------------------------
// Tax / VAT settings
// ------------------------------------------------------------
function SettingsTab() {
    const [s, setS] = useState(null);
    useEffect(() => { api.get("/payments/settings").then(({ data }) => setS(data)); }, []);
    const save = async () => {
        try {
            const { data } = await api.put("/payments/settings", s);
            setS(data);
            toast.success("Saved");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    if (!s) return <div>Loading…</div>;
    return (
        <div className="akm-card max-w-2xl p-5 space-y-4" data-testid="settings-tab">
            <Field label="Tax / VAT rate (decimal, e.g. 0.10 = 10%)">
                <input type="number" step="0.001" min="0" max="1" className="akm-input"
                       value={s.tax_rate} onChange={(e) => setS({ ...s, tax_rate: parseFloat(e.target.value) || 0 })}
                       data-testid="settings-tax" />
            </Field>
            <Field label="VAT number">
                <input className="akm-input" value={s.vat_number || ""}
                       onChange={(e) => setS({ ...s, vat_number: e.target.value })}
                       data-testid="settings-vat" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Currency">
                    <select className="akm-input" value={s.currency}
                            onChange={(e) => setS({ ...s, currency: e.target.value })}>
                        {["EUR", "USD", "GBP", "ALL"].map((c) => <option key={c}>{c}</option>)}
                    </select>
                </Field>
                <Field label="Invoice prefix">
                    <input className="akm-input" value={s.invoice_prefix}
                           onChange={(e) => setS({ ...s, invoice_prefix: e.target.value.toUpperCase() })} />
                </Field>
            </div>
            <Field label="Payment methods enabled">
                <div className="flex flex-wrap gap-2">
                    {["cash", "card", "bank_transfer", "online", "room_charge"].map((m) => (
                        <label key={m} className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-[12px]">
                            <input type="checkbox" checked={(s.payment_methods_enabled || []).includes(m)}
                                   onChange={(e) => {
                                       const arr = new Set(s.payment_methods_enabled || []);
                                       if (e.target.checked) arr.add(m); else arr.delete(m);
                                       setS({ ...s, payment_methods_enabled: Array.from(arr) });
                                   }} />
                            <span className="capitalize">{m.replace("_", " ")}</span>
                        </label>
                    ))}
                </div>
            </Field>
            <label className="flex items-center gap-2 text-[12px]">
                <input type="checkbox" checked={s.online_payment_enabled}
                       onChange={(e) => setS({ ...s, online_payment_enabled: e.target.checked })} />
                Enable online payment gateway (mocked — replace with real Stripe key)
            </label>
            <div className="flex justify-end">
                <button onClick={save} className="akm-btn-primary" data-testid="settings-save">Save settings</button>
            </div>
        </div>
    );
}
